import axios, { AxiosInstance } from 'axios';
import { Integration, CreateIntegrationRequest, ApiResponse } from '../types';

// Integrations API base URL - separate microservice
const INTEGRATIONS_API_URL = import.meta.env.VITE_INTEGRATIONS_API_URL || 'http://localhost:8788';

// Create dedicated client for integrations service
const integrationsClient: AxiosInstance = axios.create({
  baseURL: INTEGRATIONS_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
integrationsClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
integrationsClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const integrationsApi = {
  /**
   * List integrations for authenticated tenant
   * Note: tenant_id is automatically extracted from JWT token on the backend
   */
  async list(): Promise<ApiResponse<{
    integrations: Integration[];
    total: number;
    limit: number;
    offset: number;
  }>> {
    const response = await integrationsClient.get('/api/integrations');
    return response.data;
  },

  /**
   * Get integration by ID
   */
  async get(id: string): Promise<ApiResponse<Integration>> {
    const response = await integrationsClient.get(`/api/integrations/${id}`);
    return response.data;
  },

  /**
   * Create new integration
   */
  async create(data: CreateIntegrationRequest): Promise<ApiResponse<Integration>> {
    const response = await integrationsClient.post('/api/integrations', data);
    return response.data;
  },

  /**
   * Update integration
   */
  async update(id: string, data: Partial<Integration>): Promise<ApiResponse<Integration>> {
    const response = await integrationsClient.patch(`/api/integrations/${id}`, data);
    return response.data;
  },

  /**
   * Delete integration
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    const response = await integrationsClient.delete(`/api/integrations/${id}`);
    return response.data;
  },

  /**
   * Connect WhatsApp integration
   * @param attemptConnection - Set to true when user has scanned QR code
   */
  async connectWhatsApp(id: string, attemptConnection: boolean = false): Promise<ApiResponse<{
    status: string;
    qr_code?: string;
    phone_number?: string;
    connected_at?: string;
  }>> {
    const params = attemptConnection ? '?attempt_connection=true' : '';
    const response = await integrationsClient.post(`/api/integrations/${id}/whatsapp/connect${params}`);
    return response.data;
  },
};
